import { StyleMapBannerBtn } from '../typescript/component';

export const ResourceTypeMap = {
  article: 'articles',
  'case-study': 'caseStudies',
  video: 'videos',
  webinar: 'webinars',
  'white-paper': 'whitepapers'
}

export const handleScrollToHash = (hash: string, offsetHeight?: number) => {
  if (!hash) return; // Early exit if hash is not defined or empty

  const element = document.getElementById(hash);
  if (element) {
    const navbarOffset = offsetHeight || 96; // Default offset for the navbar height
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - navbarOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

export const toProperCase = (str: string) =>
  str
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// Hostnames of the Form Technologies family of sites (production + staging).
// Used to decide whether a link belongs to one of our own sites before we
// append the current locale to it. Matched exactly after stripping a leading
// "www." so unrelated domains (e.g. go.formtechnologies.com) are NOT included.
const OWN_SITE_HOSTNAMES = new Set([
  'formtechnologies.com',
  'dynacast.com',
  'dynacast.cn',
  'optimim.com',
  'signicast.com',
  'techmire.com',
  'cirex.com',
  'cirex-production.azcontentstackapps.com',
  'cirex-staging.azcontentstackapps.com',
  'dynacast-staging.azcontentstackapps.com',
  'dynacast-china-staging.azcontentstackapps.com',
  'formtechnologies-staging.azcontentstackapps.com',
  'optimim-staging.azcontentstackapps.com',
  'signicast-staging.azcontentstackapps.com',
  'techmire-staging.azcontentstackapps.com',
]);

const normalizeHost = (host: string) => host.toLowerCase().replace(/^www\./, '');

// True only when href is an absolute URL pointing directly at one of our sites.
// Relative/internal links (which Next.js already locale-routes) return false.
export const isOwnSiteUrl = (href?: string | null): boolean => {
  if (!href) return false;
  try {
    return OWN_SITE_HOSTNAMES.has(normalizeHost(new URL(href).hostname));
  } catch {
    return false;
  }
};

// Prepends the current locale as the first path segment when `href` links
// directly to one of our family sites; otherwise returns href unchanged so
// third-party links are never rewritten.
export const appendLocaleToOwnSite = (
  href?: string | null,
  locale?: string | null,
): string | undefined => {
  if (!href) return href ?? undefined;
  if (!locale || !isOwnSiteUrl(href)) return href;
  try {
    const url = new URL(href);
    const basePath = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
    url.pathname = `/${locale}${basePath}`;
    return url.toString();
  } catch {
    return href;
  }
};

export const generateUniqueId = (title: string | undefined, prefix?: string): string => {
  const normalizedTitle = title
    ? title
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
    : '';
  return `${prefix ? prefix + '-' : ''}${normalizedTitle}`.replace(/^-|-$/g, '');
};

export const getRandomItems = <T>(array: T[], numItems: number): T[] => {
  // Create a shallow copy of the array and shuffle it
  const shuffled = array.slice().sort(() => 0.5 - Math.random());
  // Return the specified number of items
  return shuffled.slice(0, numItems);
};

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

const cache: { [key: string]: CacheEntry } = {};
const requestCache = new Map<string, Promise<any>>();

// Different TTL for different types of data
export const CACHE_TTL = {
  STATIC_DATA: 24 * 60 * 60 * 1000,     // 24 hours - for content that rarely changes
  DYNAMIC_DATA: 5 * 60 * 1000,          // 5 minutes - for frequently changing data
  USER_DATA: 60 * 1000,                 // 1 minute - for user-specific data
  NAVIGATION_DATA: 2 * 60 * 60 * 1000,  // 2 hours - for header/footer/navigation
  CONTENT_DATA: 30 * 60 * 1000,         // 30 minutes - for page content
  API_RESPONSE: 10 * 60 * 1000,         // 10 minutes - for API responses
  SEARCH_RESULTS: 15 * 60 * 1000,       // 15 minutes - for search results
  DEFAULT: 30 * 60 * 1000,              // 30 minutes
} as const;

export const getCachedData = (key: string, customTTL?: number) => {
  const entry = cache[key];
  if (!entry) return null;

  const ttl = customTTL || entry.ttl || CACHE_TTL.DEFAULT;
  const isFresh = Date.now() - entry.timestamp < ttl;
  
  if (isFresh) {
    return entry.data;
  } else {
    delete cache[key]; // Clean up stale entry
    return null;
  }
};

export const setCachedData = (key: string, data: any, ttl: number = CACHE_TTL.DEFAULT) => {
  cache[key] = {
    data,
    timestamp: Date.now(),
    ttl,
  };
};

export const clearAllCachedData = () => {
  for (const key in cache) {
    delete cache[key];
  }
  requestCache.clear();
};

// Request deduplication to prevent multiple identical requests
export const deduplicatedRequest = async <T>(
  key: string, 
  requestFn: () => Promise<T>,
  ttl: number = CACHE_TTL.DEFAULT
): Promise<T> => {
  // Check cache first
  const cached = getCachedData(key, ttl);
  if (cached) {
    return cached;
  }

  // Check if request is already in progress
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  // Make new request and cache the promise
  const promise = requestFn().then(result => {
    setCachedData(key, result, ttl);
    requestCache.delete(key);
    return result;
  }).catch(error => {
    requestCache.delete(key);
    throw error;
  });

  requestCache.set(key, promise);
  return promise;
};

// Cache cleanup utility
export const cleanupExpiredCache = () => {
  const now = Date.now();
  for (const key in cache) {
    const entry = cache[key];
    if (now - entry.timestamp > entry.ttl) {
      delete cache[key];
    }
  }
};

// Run cleanup every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(cleanupExpiredCache, 5 * 60 * 1000);
}

export const generateRandomId = () => 'pagination-' + Math.random().toString(36).substring(2, 15);

export function getColorBrandSpecific(style: StyleMapBannerBtn): string {
  if (style === StyleMapBannerBtn.BRAND_SPECIFIC) {
    return 'var(--brand-primary-500)';
  }
  return 'var(--charcoal-500)';
}

export const getCachedAsset = (assetUrl?: string, params?: {
  width?: number;
  height?: number;
  quality?: number;
}) => {
  var finalUrl = assetUrl ? assetUrl.replace("https://azure-na-assets.contentstack.com/", "/cms-assets/").replace("https://azure-na-images.contentstack.com", "/cms-images") : "";
  const _params = [];

  if (params && Object.entries(params).length) {
    for (let [name, value] of Object.entries(params)) {
      _params.push(`${name}=${value}`);
    }
  }

  if (_params.length)
    finalUrl += '?' + _params.join('&');

  return finalUrl;
}

export const getCdnCachedData = async <T>(
  key: string,
  params: Record<string, string>,
  fallback: () => Promise<T>,
  ttl: number = CACHE_TTL.DEFAULT
): Promise<T> => {
  // Never use an already-deployed site while Next.js is building.
  if (process.env.NEXT_PHASE === 'phase-production-build') {
    return fallback();
  }

  const query = new URLSearchParams(params).toString();
  const path = `/api/cms-data/${key}${query ? `?${query}` : ''}`;

  let url = path;

  if (typeof window === 'undefined') {
    const baseUrl = process.env.NEXT_PUBLIC_HOSTED_URL?.replace(/\/$/, '')
      || (process.env.NODE_ENV === 'development' ? `http://localhost:${process.env.PORT || 3000}` : '');

    if (!baseUrl) {
      return fallback();
    }

    url = `${baseUrl}${path}`;
  }

  const request = async (): Promise<T> => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`CMS data request failed: ${response.status} ${url}`);
    }

    return response.json() as Promise<T>;
  };

  // Keep the current memory cache for localhost only.
  if (process.env.NODE_ENV === 'development') {
    return deduplicatedRequest(`cms_${key}_${query}`, request, ttl);
  }

  try {
    return await request();
  } catch (error) {
    console.error(`CDN CMS data request failed for ${key}, falling back to Contentstack`, error);
    return fallback();
  }
};

export const getContentstackCacheTag = (contentType: string, locale: string) =>
  `cs-${contentType.replace(/_/g, '-')}-${locale}`;

export const getContentstackEntryCacheTag = (uid: string, locale: string) =>
  `cs-entry-${uid}-${locale}`;